// File: src/pages/Performance.jsx
// BUCKET: Experience & Conversion
// "Where friction happens, how to fix it"

import React, { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  MousePointer,
  ArrowRight,
  Zap,
  Eye,
  ArrowLeft,
  Activity
} from 'lucide-react';
import { Spinner } from '@shopify/polaris';
import LayoutWrapper from '../components/Layout/Layout';
import { AuthContext } from '../context/AuthContext';
import useData from '../hooks/useData';
import GA4SetupPrompt from '../components/GA4SetupPrompt';
import './DashboardBucket.css';
import DateRangePills from '../components/ui/DateRangePills';

// Helper function to format seconds into minutes and seconds
const formatDuration = (totalSeconds) => {
  if (totalSeconds === undefined || totalSeconds === null) return 'N/A';
  const seconds = Math.round(totalSeconds);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0 && remainingSeconds > 0) return `${minutes}m ${remainingSeconds}s`;
  if (minutes > 0) return `${minutes}m`;
  if (remainingSeconds > 0) return `${remainingSeconds}s`;
  return '0s';
};

// Skeleton loader for 2 KPI cards
const KPISkeleton = () => (
  <>
    <div className="stat-item stat-item--centered skeleton">
      <div className="skeleton-icon" style={{ width: '20px', height: '20px', background: '#e0e0e0', borderRadius: '4px' }}></div>
      <div className="stat-content">
        <div className="skeleton-value" style={{ width: '60px', height: '20px', background: '#e0e0e0', borderRadius: '4px', marginBottom: '4px' }}></div>
        <div className="skeleton-label" style={{ width: '80px', height: '14px', background: '#f0f0f0', borderRadius: '4px' }}></div>
      </div>
    </div>
    <div className="stat-item stat-item--centered skeleton">
      <div className="skeleton-icon" style={{ width: '20px', height: '20px', background: '#e0e0e0', borderRadius: '4px' }}></div>
      <div className="stat-content">
        <div className="skeleton-value" style={{ width: '50px', height: '20px', background: '#e0e0e0', borderRadius: '4px', marginBottom: '4px' }}></div>
        <div className="skeleton-label" style={{ width: '70px', height: '14px', background: '#f0f0f0', borderRadius: '4px' }}></div>
      </div>
    </div>
  </>
);

export default function Performance() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

  // Get site_id from sessionStorage
  const [siteId, setSiteId] = useState(() => 
    sessionStorage.getItem('site_id') || sessionStorage.getItem('siteId') || sessionStorage.getItem('activeSiteId') || ''
  );

  useEffect(() => {
    const read = () => sessionStorage.getItem('site_id') || sessionStorage.getItem('siteId') || sessionStorage.getItem('activeSiteId') || '';
    if (!siteId) setSiteId(read());
    const onStorage = (e) => { 
      if (['site_id', 'siteId', 'activeSiteId'].includes(e.key)) setSiteId(read()); 
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [siteId]);

  const [daysRange, setDaysRange] = useState('7');
  const handleRangeChange = useCallback((value) => setDaysRange(value), []);

  // Use cached data for user sites
  const { loading: sitesLoading, error: sitesError } = useData(
    `sites:${token}`,
    () => fetch(`${API_URL}/api/sites`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token, API_URL] }
  );

  // FIXED: Use /integrations/status endpoint instead of /integrations/property
  const { data: ga4Status, loading: ga4StatusLoading, error: ga4StatusError } = useData(
    `ga4Status:${token}`,
    async () => {
      const res = await fetch(`${API_URL}/integrations/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return { connected: false };
      return res.json();
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token, API_URL] }
  );
  const ga4Connected = ga4Status?.connected || false;

  // FIXED: Use ga4Connected in cache key instead of savedPropertyId
  const { data: ga4BatchData, loading: ga4Loading, error: ga4Error } = useData(
    `ga4Batch:${ga4Connected}:${siteId}:${daysRange}`,
    () => {
      // Build URL with site_id for fallback support
      const params = new URLSearchParams({ days: daysRange });
      if (siteId) params.append('site_id', siteId);
      
      return fetch(`${API_URL}/api/business/analytics/batch?${params}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      }).then(r => r.json());
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token, API_URL, ga4Connected, siteId, daysRange] }
  );
  const ga4Metrics = ga4BatchData?.performance;

  // 2 widget cards for Experience & Conversion bucket
  const widgets = useMemo(() => ([
    {
      title: 'Conversion Funnel',
      description: 'Where people leave, sales funnel breakdown & cart abandonment',
      detailsPath: '/dropoffs',
      icon: ArrowDown,
      bgGradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    },
    {
      title: 'UX Insights',
      description: 'Friction points, rage clicks, heatmaps & engagement patterns',
      detailsPath: '/UXInsightsPage',
      icon: MousePointer,
      bgGradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
    },
  ]), []);

  if (!token) {
    return (
      <LayoutWrapper>
        <div className="bucket-page">
          <div className="bucket-container">
            <div className="auth-required">
              <div className="auth-icon">
                <Zap size={48} />
              </div>
              <h2>Authentication Required</h2>
              <p>Please log in to view experience and conversion insights.</p>
              <button className="auth-btn" onClick={() => navigate('/login')}>
                Sign In
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  // FIXED: use ga4StatusLoading instead of savedPropertyLoading
  if (sitesLoading || ga4StatusLoading) {
    return (
      <LayoutWrapper>
        <div className="bucket-page">
          <div className="bucket-container">
            <div className="back-button-wrapper">
              <button className="back-icon-button" onClick={() => navigate('/dashboard')}>
                <ArrowLeft size={20} />
              </button>
            </div>
            <div className="bucket-loading">
              <Spinner accessibilityLabel="Loading sites" size="large" />
              <h2>Loading Site Information...</h2>
              <p>Fetching your connected websites.</p>
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  // FIXED: use ga4StatusError instead of savedPropertyError
  if (sitesError || ga4StatusError) {
    return (
      <LayoutWrapper>
        <div className="bucket-page">
          <div className="bucket-container">
            <div className="back-button-wrapper">
              <button className="back-icon-button" onClick={() => navigate('/dashboard')}>
                <ArrowLeft size={20} />
              </button>
            </div>
            <div className="bucket-error">
              <div className="error-icon">
                <Eye size={48} />
              </div>
              <h2>Error Loading Sites</h2>
              <p>{sitesError || ga4StatusError}</p>
              <button className="auth-btn" onClick={() => window.location.reload()}>
                Retry
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <div className="bucket-page">
        <div className="bucket-container">
          <div className="back-button-wrapper">
            <button className="back-icon-button" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={20} />
            </button>
          </div>
          
          <div className="bucket-header">
            <div className="header-content">
              <div className="header-badge">
                <Zap size={16} />
                <span>Experience & Conversion</span>
              </div>
              <h1>Experience & Conversion</h1>
              <p>Where friction happens and how to fix it. Remove barriers on the path to purchase.</p>
            </div>
            
            <div className="date-range-wrapper">
              <DateRangePills value={daysRange} onChange={handleRangeChange} />
            </div>
          </div>
          
          {/* GA4 Setup Prompt - shown when GA4 not connected (only after loading) */}
          {!ga4StatusLoading && !ga4Connected && (
            <GA4SetupPrompt 
              variant="inline" 
              feature="detailed engagement metrics"
            />
          )}
          
          {/* 2 KPI Cards - Centered */}
          <div className="quick-stats quick-stats--center-2">
            {ga4Loading ? (
              <KPISkeleton />
            ) : ga4Error ? (
              <div className="stats-error-indicator">
                <p>Error: {ga4Error}</p>
              </div>
            ) : (
              <>
                <div className="stat-item stat-item--centered">
                  <Activity className="stat-icon" size={20} />
                  <div className="stat-content">
                    <span className="stat-value">
                      {ga4Metrics?.engagement_rate_pct !== undefined ? `${ga4Metrics.engagement_rate_pct}%` : 'N/A'}
                    </span>
                    <span className="stat-label">Engagement Rate</span>
                  </div>
                </div>
                <div className="stat-item stat-item--centered">
                  <MousePointer className="stat-icon" size={20} />
                  <div className="stat-content">
                    <span className="stat-value">
                      {ga4Metrics?.avg_engagement_duration_sec !== undefined 
                        ? formatDuration(ga4Metrics.avg_engagement_duration_sec) 
                        : 'N/A'}
                    </span>
                    <span className="stat-label">Avg Engagement Time</span>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <p className="focus-line">Focus: Removing friction from the purchase path.</p>
          
          {/* Widget Cards */}
          <div className="widgets-grid widgets-grid--center-2">
            {widgets.map(({ title, description, detailsPath, icon: Icon, bgGradient }) => (
              <div key={title} className="widget-card">
                <div className="widget-header">
                  <div className="widget-icon" style={{ background: bgGradient }}>
                    <Icon size={24} color="white" />
                  </div>
                </div>
                <div className="widget-content">
                  <h3>{title}</h3>
                  <p className="widget-description">{description}</p>
                </div>
                <div className="widget-footer">
                  <button className="widget-btn" onClick={() => navigate(detailsPath)}>
                    Analyze Now
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
}